// src/components/subscription/TrialBanner.js
import React, { useState } from 'react';
import { Clock, Zap, AlertCircle, X } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { useNavigate } from 'react-router-dom';

/**
 * Trial countdown banner
 * Shows subscription status at top of app
 * Only displays when payment enforcement is enabled
 */
const TrialBanner = ({ userId }) => {
  const [isClosed, setIsClosed] = useState(false);
  const navigate = useNavigate();
  const subscriptionState = useSubscription(userId);
  
  const {
    loading,
    isOnTrial,
    daysLeftInTrial,
    enforcementEnabled,
    status,
  } = subscriptionState;

  // Don't show in beta mode or while loading
  if (!enforcementEnabled || loading) {
    return null;
  }

  // Don't show for active subscribers
  if (status === 'active') {
    return null;
  }

  // Don't show if user closed the banner
  if (isClosed) {
    return null;
  }

  // Determine banner style based on status
  const getBannerConfig = () => {
    if (isOnTrial) {
      // Trial user

      return {
        icon: Clock,
        bgColor: 'bg-app-info',
        bgColorLight: 'bg-opacity-10',
        borderColor: 'border-app-info',
        textColor: 'text-app-info',
        iconColor: 'text-app-info',
        message: `${daysLeftInTrial} ${daysLeftInTrial === 1 ? 'day' : 'days'} left in your free trial`,
        showButton: true,
        buttonText: 'Upgrade Now',
      };
    } else {
      // No access
      return {
        icon: AlertCircle,
        bgColor: 'bg-app-error',
        bgColorLight: 'bg-opacity-15',
        borderColor: 'border-app-error',
        textColor: 'text-app-error',
        iconColor: 'text-app-error',
        message: 'Your free trial has ended',
        showButton: true,
        buttonText: 'Finish Setup',
      };
    }
  };

  const config = getBannerConfig();
  const Icon = config.icon;

  return (
    <div
      className={`
        ${config.bgColor} ${config.bgColorLight} ${config.borderColor} ${config.textColor}
        border-b px-app-2xl py-app-md
        flex items-center justify-between gap-app-lg
        animate-in fade-in slide-in-from-top-2 duration-app-slow
        group
      `}
    >
      <div className="flex items-center gap-app-md flex-1">
        <Icon className={`w-comp-2xl h-comp-2xl ${config.iconColor} flex-shrink-0`} />
        <span className="text-app-sm font-medium">
          {config.message}
        </span>
      </div>

      <div className="flex items-center gap-app-md flex-shrink-0">
        {config.showButton && (
          <button
            onClick={() => navigate('/pricing')}
            className="btn-primary"
          >
            <Zap className="w-comp-lg h-comp-lg" />
            {config.buttonText}
          </button>
        )}

        {isOnTrial && (
          <button
            onClick={() => setIsClosed(true)}
            className={`
              w-comp-2xl h-comp-2xl
              flex items-center justify-center
              text-inherit opacity-0 group-hover:opacity-100
              transition-opacity duration-app-normal
              hover:bg-black hover:bg-opacity-10 rounded-app-sm
            `}
            aria-label="Close banner"
          >
            <X className="w-comp-lg h-comp-lg" />
          </button>
        )}
      </div>
    </div>
  );
};

export default TrialBanner;